/**
 * @fileoverview
 * Base set of pure functions for permissions. Used in the root level components and in the router container.
 */

import { Role } from '@/hooks/use-permissions/use-permissions.hook.core';

/**
 * Determines if a user has the necessary permissions to access consumer-level pages.
 *
 * This function checks if the user has Consumer role privileges.
 *
 * @param {Role[]} roles - Array of user roles to check against
 * @returns {boolean} Returns true if the user has Consumer role, false otherwise
 */
export const canAccessConsumerPage = (roles: Role[]) => {
  return roles.includes(Role.Consumer) || roles.includes(Role.Admin);
};

/**
 * Determines if a user has the necessary permissions to access maintainer-level pages.
 *
 * This function checks if the user has either Maintainer or Admin role privileges.
 * Maintainer pages typically contain functionality for managing and maintaining
 * system configurations, while Admin users have universal access.
 *
 * @param {Role[]} roles - Array of user roles to check against
 * @returns {boolean} Returns true if the user has Maintainer or Admin role, false otherwise
 */
export const canAccessMaintainerPage = (roles: Role[]) => {
  return roles.includes(Role.Maintainer) || roles.includes(Role.Admin);
};

/**
 * Determines if a user has the necessary permissions to access developer-level pages.
 *
 * This function checks if the user has Admin and (if current env is suitable) Maintainer role privileges. Developer pages
 * contain the most sensitive functionality and are restricted to select roles only.
 * This represents the highest level of access in the application hierarchy.
 *
 * @param {Role[]} roles - Array of user roles to check against
 * @param {'dev' | 'qa' | 'test' | 'prod'} currentEnv - Current environment string
 * @returns {boolean} Returns true if the user has Admin role, false otherwise
 */
export const canAccessDeveloperPage = (roles: Role[], currentEnv: 'dev' | 'qa' | 'test' | 'prod') => {
  return (
    currentEnv === 'dev' &&
    (roles.includes(Role.Admin) || roles.includes(Role.Consumer) || roles.includes(Role.Maintainer))
  );
};

/**
 * Validates if a user has any recognized role within the application.
 *
 * This function performs a comprehensive check to determine if the user
 * has at least one valid role (Admin, Maintainer, or Consumer). This is
 * typically used as a gate-keeper function to ensure users have some level
 * of authenticated access before allowing them into the application.
 *
 * @param {Role[]} roles - Array of user roles to validate
 * @returns {boolean} Returns true if the user has at least one valid role, false otherwise
 */
export const hasValidRole = (roles: Role[]) => {
  return roles.includes(Role.Admin) || roles.includes(Role.Maintainer) || roles.includes(Role.Consumer);
};
